import React, { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

interface AnimatedNumberProps {
    value: number | undefined;
    format?: (value: number) => string;
    fallback?: string; // Optional custom fallback, defaults to "."
}

export default function AnimatedNumber({
                                           value,
                                           format,
                                           fallback = ".",
                                       }: AnimatedNumberProps) {
    const isFirstRender = useRef(true);
    const motionValue = useMotionValue<number>(0);

    const springValue = useSpring(motionValue, {
        stiffness: 300,
        damping: 30,
    });

    const formatted = useTransform(springValue, (latest) => {
        if (value === undefined || isNaN(value) || isNaN(latest) || value === null) {
            return fallback;
        }
        return format ? format(latest) : latest.toFixed(2);
    });

    useEffect(() => {
        if (value !== undefined && !isNaN(value) && value !== null) {
            if (isFirstRender.current) {
                // On first render, jump directly to value without animation
                motionValue.jump(value);
                isFirstRender.current = false;
            } else {
                // On subsequent renders, animate to new value
                motionValue.set(value);
            }
        }
    }, [value, motionValue]);

    return <motion.span>{formatted}</motion.span>;
}
